import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined, RocketOutlined, CloudServerOutlined, DatabaseOutlined, DesktopOutlined } from '@ant-design/icons';

interface ProjectOption {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  route: string;
  color: string;
  bgColor: string;
}

const CreateProject: React.FC = () => {
  const navigate = useNavigate();

  const projectOptions: ProjectOption[] = [
    {
      id: 'deploy-app',
      title: 'Deploy App',
      description: 'Deploy your application from a Git repository. Supports Node.js, Python, .NET Core, React, Vue, and more.',
      icon: <RocketOutlined className="text-4xl" />,
      route: '/deploy-apps/create',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      id: 'app-server',
      title: 'App Server',
      description: 'Deploy a pre-configured application server stack. LAMP, Node.js, MEAN, Django, or Nginx.',
      icon: <CloudServerOutlined className="text-4xl" />,
      route: '/stacks/create',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
    },
    {
      id: 'database',
      title: 'Database',
      description: 'Create and manage databases. PostgreSQL, MySQL, MongoDB, Redis, and more.',
      icon: <DatabaseOutlined className="text-4xl" />,
      route: '/resources/create-database',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      id: 'virtual-machine',
      title: 'Virtual Machine',
      description: 'Launch a virtual machine with your preferred operating system and configuration.',
      icon: <DesktopOutlined className="text-4xl" />,
      route: '/resources/create-vm',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
  ];

  const handleOptionClick = (route: string) => {
    navigate(route);
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-6">
      <button
        onClick={() => navigate('/projects')}
        className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6 transition-colors"
      >
        <ArrowLeftOutlined />
        <span className="text-sm font-medium">Back to Projects</span>
      </button>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Create New Project</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Choose the type of resource you want to create and deploy
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {projectOptions.map((option) => (
          <button
            key={option.id}
            onClick={() => handleOptionClick(option.route)}
            className={`${option.bgColor} dark:bg-gray-800 border-2 border-transparent hover:border-gray-300 dark:hover:border-gray-600 rounded-lg p-6 text-left transition-all hover:shadow-lg group`}
          >
            <div className="flex items-start gap-4">
              <div className={`${option.color} flex-shrink-0`}>
                {option.icon}
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-gray-700 dark:group-hover:text-gray-300">
                  {option.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  {option.description}
                </p>
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white">
              <span>Get started</span>
              <ArrowLeftOutlined className="ml-2 rotate-180 text-xs" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default CreateProject;
